/**
 * Theme-aware styles and overrides for react-big-calendar (Issue #892 & Issue #915).
 * Provides high-contrast, WCAG AA compliant colors for both light and dark modes,
 * plus mobile-responsive toolbar and cell sizing:
 * - Dims out-of-month days (.rbc-off-range-bg) with accessible text contrast.
 * - Highlights current day (.rbc-today) with a distinct primary badge and soft tint.
 * - Styles toolbar buttons, grid lines, agenda tables, and overlay popups using theme tokens.
 */

const getMobileResponsiveCalendarSx = () => ({
    '@media (max-width: 767px)': {
        '& .rbc-toolbar': {
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'stretch',
        },
        '& .rbc-toolbar .rbc-toolbar-label': {
            padding: '2px 0',
            textAlign: 'center',
            order: -1,
        },
        '& .rbc-btn-group': {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            width: '100%',
        },
        '& .rbc-btn-group + .rbc-btn-group, & .rbc-btn-group + button': {
            marginLeft: '0 !important',
        },
        '& .rbc-toolbar button': {
            flex: '1 1 auto',
            padding: '6px 10px',
            fontSize: '0.8125rem',
        },
    },
    '@media (max-width: 600px)': {
        '& .rbc-header': {
            fontSize: '0.75rem',
            padding: '4px 1px',
        },
        '& .rbc-date-cell': {
            paddingRight: '3px',
            fontSize: '0.75rem',
        },
        '& .rbc-event': {
            padding: '1px 3px',
            fontSize: '0.72rem !important',
            lineHeight: 1.2,
        },
    },
});

const getLightCalendarSx = (theme) => ({
    '& .rbc-calendar': {
        fontFamily: theme?.typography?.fontFamily || 'inherit',
        color: '#1e293b',
    },
    '& .rbc-header': {
        backgroundColor: '#f8fafc',
        color: '#1e293b',
        borderColor: '#cbd5e1',
        padding: '6px 3px',
        fontWeight: 700,
    },
    '& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view table.rbc-agenda-table': {
        borderColor: '#cbd5e1',
        backgroundColor: '#ffffff',
    },
    '& .rbc-today': {
        backgroundColor: 'rgba(26, 58, 92, 0.06)',
    },
    '& .rbc-today .rbc-button-link': {
        backgroundColor: theme?.palette?.primary?.main || '#1a3a5c',
        color: '#ffffff',
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
    },
    '& .rbc-off-range-bg': {
        backgroundColor: '#f1f5f9',
    },
    '& .rbc-off-range, & .rbc-off-range .rbc-button-link': {
        color: '#475569',
    },
    '& .rbc-toolbar': {
        marginBottom: '16px',
        gap: '8px',
        '& .rbc-toolbar-label': {
            color: '#1e293b',
            fontWeight: 700,
            fontSize: '1.1rem',
        },
        '& button': {
            color: '#1e293b',
            borderColor: '#cbd5e1',
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            padding: '6px 14px',
            fontWeight: 600,
            transition: 'all 0.2s ease',
            '&:hover, &:focus': {
                backgroundColor: '#f1f5f9',
                borderColor: '#94a3b8',
                color: '#0f172a',
            },
            '&:active, &.rbc-active': {
                backgroundColor: '#1a3a5c !important',
                borderColor: '#1a3a5c !important',
                color: '#ffffff !important',
                fontWeight: '700 !important',
            },
        },
    },
    '& .rbc-show-more': {
        backgroundColor: 'rgba(26, 58, 92, 0.1) !important',
        color: '#1a3a5c !important',
        borderRadius: '4px',
        padding: '1px 4px',
        fontWeight: 700,
    },
    '& .rbc-agenda-view table.rbc-agenda-table tbody > tr': {
        display: 'table-row !important',
    },
    ...getMobileResponsiveCalendarSx(),
});

const getDarkGridSx = (theme) => ({
    '& .rbc-calendar': {
        fontFamily: theme?.typography?.fontFamily || 'inherit',
        color: '#F8FAFC',
    },
    '& .rbc-month-view': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        backgroundColor: theme?.palette?.background?.paper || '#111C2E',
    },
    '& .rbc-month-row': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '& .rbc-month-row + .rbc-month-row': {
        borderTopColor: 'rgba(255, 255, 255, 0.12)',
    },
    '& .rbc-day-bg + .rbc-day-bg': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
    '& .rbc-off-range-bg': {
        backgroundColor: 'rgba(0, 0, 0, 0.35) !important',
    },
    '& .rbc-off-range, & .rbc-off-range .rbc-button-link': {
        color: '#64748B !important',
    },
    '& .rbc-today': {
        backgroundColor: 'rgba(144, 202, 249, 0.12) !important',
    },
    '& .rbc-today .rbc-button-link, & .rbc-date-cell.rbc-now .rbc-button-link': {
        backgroundColor: '#1976d2 !important',
        color: '#ffffff !important',
        borderRadius: '50% !important',
        width: '24px !important',
        height: '24px !important',
        display: 'inline-flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        fontWeight: '700 !important',
        marginRight: '2px',
        marginTop: '2px',
    },
    '& .rbc-date-cell': {
        paddingRight: '6px',
        paddingTop: '4px',
        color: '#F8FAFC',
        '& > a, & > button': {
            color: 'inherit',
            fontWeight: 500,
        },
    },
    '& .rbc-header': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        color: '#F8FAFC',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        padding: '6px 3px',
        fontWeight: 600,
    },
    '& .rbc-header + .rbc-header': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
});

const getDarkToolbarAndViewsSx = (theme) => ({
    '& .rbc-toolbar': {
        marginBottom: '16px',
        gap: '8px',
        '& .rbc-toolbar-label': {
            color: '#F8FAFC',
            fontWeight: 700,
            fontSize: '1.1rem',
        },
        '& button': {
            color: '#F8FAFC',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '6px',
            padding: '6px 14px',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                borderColor: 'rgba(255, 255, 255, 0.35)',
                color: '#ffffff',
            },
            '&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                borderColor: 'rgba(255, 255, 255, 0.35)',
                color: '#ffffff',
            },
            '&:active, &.rbc-active': {
                backgroundColor: '#90caf9 !important',
                borderColor: '#90caf9 !important',
                color: '#0B131E !important',
                fontWeight: '700 !important',
                boxShadow: '0 2px 8px rgba(144, 202, 249, 0.3)',
                '&:hover': {
                    backgroundColor: '#bbdefb !important',
                    borderColor: '#bbdefb !important',
                },
            },
        },
    },
    '& .rbc-time-view': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        backgroundColor: theme?.palette?.background?.paper || '#111C2E',
        '& .rbc-time-header-content': {
            borderLeftColor: 'rgba(255, 255, 255, 0.12)',
        },
        '& .rbc-time-content': {
            borderTopColor: 'rgba(255, 255, 255, 0.15)',
            '& > * + * > *': {
                borderLeftColor: 'rgba(255, 255, 255, 0.12)',
            },
        },
        '& .rbc-timeslot-group': {
            borderBottomColor: 'rgba(255, 255, 255, 0.08)',
        },
        '& .rbc-time-slot': {
            borderTopColor: 'rgba(255, 255, 255, 0.04)',
        },
        '& .rbc-time-gutter': {
            color: '#94A3B8',
        },
        '& .rbc-allday-cell + .rbc-allday-cell': {
            borderLeftColor: 'rgba(255, 255, 255, 0.12)',
        },
    },
    '& .rbc-agenda-view table.rbc-agenda-table': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        '& thead > tr > th': {
            borderColor: 'rgba(255, 255, 255, 0.15)',
            color: '#F8FAFC',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
        },
        '& tbody > tr': {
            display: 'table-row !important',
        },
        '& tbody > tr > td': {
            color: 'inherit',
        },
        '& tbody > tr + tr': {
            borderTopColor: 'rgba(255, 255, 255, 0.08)',
        },
        '& tbody > tr > td + td': {
            borderLeftColor: 'rgba(255, 255, 255, 0.08)',
        },
    },
    '& .rbc-show-more': {
        backgroundColor: 'rgba(144, 202, 249, 0.15) !important',
        color: '#90caf9 !important',
        borderRadius: '4px',
        padding: '1px 4px',
        fontWeight: 600,
        '&:hover, &:focus': {
            backgroundColor: 'rgba(144, 202, 249, 0.25) !important',
            color: '#e3f2fd !important',
        },
    },
    '& .rbc-overlay': {
        backgroundColor: `${theme?.palette?.background?.paper || '#111C2E'} !important`,
        borderColor: 'rgba(255, 255, 255, 0.18) !important',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6) !important',
        borderRadius: '8px !important',
        color: '#F8FAFC !important',
        '& .rbc-overlay-header': {
            borderBottomColor: 'rgba(255, 255, 255, 0.12) !important',
            color: '#F8FAFC !important',
            fontWeight: 600,
        },
    },
    ...getMobileResponsiveCalendarSx(),
});

export const getCalendarSx = (theme) => {
    if (theme?.palette?.mode !== 'dark') {
        return getLightCalendarSx(theme);
    }
    return {
        ...getDarkGridSx(theme),
        ...getDarkToolbarAndViewsSx(theme),
    };
};

const LIGHT_GLOBAL_OVERRIDES = {
    '.rbc-today': {
        backgroundColor: 'rgba(26, 58, 92, 0.06)',
    },
    '.rbc-today .rbc-button-link': {
        backgroundColor: '#1a3a5c',
        color: '#ffffff',
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
    },
    '.rbc-off-range-bg': {
        backgroundColor: '#f1f5f9',
    },
    '.rbc-off-range': {
        color: '#475569',
    },
};

const DARK_GLOBAL_OVERRIDES = {
    '.rbc-calendar': {
        color: '#F8FAFC',
    },
    '.rbc-month-view': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        backgroundColor: '#111C2E',
    },
    '.rbc-month-row': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-month-row + .rbc-month-row': {
        borderTopColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-day-bg + .rbc-day-bg': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-off-range-bg': {
        backgroundColor: 'rgba(0, 0, 0, 0.35) !important',
    },
    '.rbc-off-range, .rbc-off-range .rbc-button-link': {
        color: '#64748B !important',
    },
    '.rbc-today': {
        backgroundColor: 'rgba(144, 202, 249, 0.12) !important',
    },
    '.rbc-today .rbc-button-link, .rbc-date-cell.rbc-now .rbc-button-link': {
        backgroundColor: '#1976d2 !important',
        color: '#ffffff !important',
        borderRadius: '50% !important',
        width: '24px !important',
        height: '24px !important',
        display: 'inline-flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        fontWeight: '700 !important',
        marginRight: '2px',
        marginTop: '2px',
    },
    '.rbc-date-cell': {
        paddingRight: '6px',
        paddingTop: '4px',
        color: '#F8FAFC',
    },
    '.rbc-header': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        color: '#F8FAFC',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        padding: '6px 3px',
        fontWeight: 600,
    },
    '.rbc-header + .rbc-header': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-toolbar': {
        marginBottom: '16px',
    },
    '.rbc-toolbar .rbc-toolbar-label': {
        color: '#F8FAFC',
        fontWeight: 700,
        fontSize: '1.1rem',
    },
    '.rbc-toolbar button': {
        color: '#F8FAFC',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px',
        padding: '6px 14px',
        transition: 'all 0.2s ease',
    },
    '.rbc-toolbar button:hover, .rbc-toolbar button:focus': {
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        borderColor: 'rgba(255, 255, 255, 0.35)',
        color: '#ffffff',
    },
    '.rbc-toolbar button:active, .rbc-toolbar button.rbc-active': {
        backgroundColor: '#90caf9 !important',
        borderColor: '#90caf9 !important',
        color: '#0B131E !important',
        fontWeight: '700 !important',
        boxShadow: '0 2px 8px rgba(144, 202, 249, 0.3)',
    },
    '.rbc-time-view': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        backgroundColor: '#111C2E',
    },
    '.rbc-time-view .rbc-time-header-content': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-time-view .rbc-time-content': {
        borderTopColor: 'rgba(255, 255, 255, 0.15)',
    },
    '.rbc-time-view .rbc-time-content > * + * > *': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-timeslot-group': {
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    },
    '.rbc-time-slot': {
        borderTopColor: 'rgba(255, 255, 255, 0.04)',
    },
    '.rbc-time-gutter': {
        color: '#94A3B8',
    },
    '.rbc-allday-cell + .rbc-allday-cell': {
        borderLeftColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-agenda-view table.rbc-agenda-table': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '.rbc-agenda-view table.rbc-agenda-table thead > tr > th': {
        borderColor: 'rgba(255, 255, 255, 0.15)',
        color: '#F8FAFC',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
    '.rbc-agenda-view table.rbc-agenda-table tbody > tr > td': {
        color: 'inherit',
    },
    '.rbc-agenda-view table.rbc-agenda-table tbody > tr + tr': {
        borderTopColor: 'rgba(255, 255, 255, 0.08)',
    },
    '.rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td': {
        borderLeftColor: 'rgba(255, 255, 255, 0.08)',
    },
    '.rbc-show-more': {
        backgroundColor: 'rgba(144, 202, 249, 0.15) !important',
        color: '#90caf9 !important',
        borderRadius: '4px',
        padding: '1px 4px',
        fontWeight: 600,
    },
    '.rbc-overlay': {
        backgroundColor: '#111C2E !important',
        borderColor: 'rgba(255, 255, 255, 0.18) !important',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6) !important',
        borderRadius: '8px !important',
        color: '#F8FAFC !important',
    },
    '.rbc-overlay .rbc-overlay-header': {
        borderBottomColor: 'rgba(255, 255, 255, 0.12) !important',
        color: '#F8FAFC !important',
        fontWeight: 600,
    },
};

export const getCalendarGlobalOverrides = (isDark) => {
    return isDark ? DARK_GLOBAL_OVERRIDES : LIGHT_GLOBAL_OVERRIDES;
};
